import React from 'react';
import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import academicTutorAvif from './images/academic-tutor.avif';
import artsAndCraftsAvif from './images/arts-and-crafts.avif';
import languageLearningAvif from './images/language-learning.avif';
import lifeSkillsAvif from './images/life-skills.avif';
import mommyAndMeAvif from './images/mommy-and-me.avif';
import musicAndDanceAvif from './images/music-and-dance.avif';
import specialNeedsAvif from './images/special-needs.avif';
import sportsAndFitnessAvif from './images/sports-and-fitness.avif';
import stemAndCodingAvif from './images/stem-and-coding.avif';
import teamSportsAvif from './images/team-sports.avif';

import academicTutorJpg from './images/academic-tutor.jpg';
import artsAndCraftsJpg from './images/arts-and-crafts.jpg';
import languageLearningJpg from './images/language-learning.jpg';
import lifeSkillsJpg from './images/life-skills.jpg';
import mommyAndMeJpg from './images/mommy-and-me.jpg';
import musicAndDanceJpg from './images/music-and-dance.jpg';
import specialNeedsJpg from './images/special-needs.jpg';
import sportsAndFitnessJpg from './images/sports-and-fitness.jpg';
import stemAndCodingJpg from './images/stem-and-coding.jpg';
import teamSportsJpg from './images/team-sports.jpg';

import css from './SectionCategories.module.css';

// Category key -> { avif, jpg }. Keys match the Console category ids (categoryLevel1). Every tile
// ships BOTH formats: modern browsers download the small AVIF, everything else the universal JPEG
// fallback (see the <picture> below). AVIF-only broke the tiles in browsers that can't decode it
// (iOS Safari <16, many in-app WebViews) -- names showed but images were broken (client report).
// Categories without an entry still render (brand-tinted placeholder tile), just without a photo.
export const CATEGORY_IMAGES = {
  'academic-tutor': { avif: academicTutorAvif, jpg: academicTutorJpg },
  'arts-and-crafts': { avif: artsAndCraftsAvif, jpg: artsAndCraftsJpg },
  'language-learning': { avif: languageLearningAvif, jpg: languageLearningJpg },
  'life-skills': { avif: lifeSkillsAvif, jpg: lifeSkillsJpg },
  'mommy-and-me': { avif: mommyAndMeAvif, jpg: mommyAndMeJpg },
  'music-and-dance': { avif: musicAndDanceAvif, jpg: musicAndDanceJpg },
  'special-needs': { avif: specialNeedsAvif, jpg: specialNeedsJpg },
  'sports-and-fitness': { avif: sportsAndFitnessAvif, jpg: sportsAndFitnessJpg },
  'stem-and-coding': { avif: stemAndCodingAvif, jpg: stemAndCodingJpg },
  'team-sports': { avif: teamSportsAvif, jpg: teamSportsJpg },
};

/**
 * Category grid sourced from the hosted-asset config (NOT hardcoded). Prefers the built-in
 * categories (config.categoryConfiguration.categories -> ?pub_categoryLevel1); falls back to the
 * custom `category` listing field's options (-> ?pub_category) if that's how the taxonomy is set up.
 * Renders nothing if neither is configured.
 */
const resolveCategories = config => {
  const builtIn = config?.categoryConfiguration?.categories || [];
  if (builtIn.length > 0) {
    // The search schema is fixed to categoryLevel1 (see util/configHelpers).
    return { param: 'pub_categoryLevel1', items: builtIn.map(c => ({ key: c.id, label: c.name })) };
  }
  const fields = config?.listing?.listingFields || [];
  const categoryField = fields.find(f => f.key === 'category');
  const options = categoryField?.enumOptions || [];
  return { param: 'pub_category', items: options.map(o => ({ key: o.option, label: o.label })) };
};

const SectionCategories = () => {
  const config = useConfiguration();
  const { param, items } = resolveCategories(config);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <div className={css.header}>
          <h2 className={css.heading}>
            <FormattedMessage id="SectionCategories.heading" />
          </h2>
          <NamedLink name="SearchPage" className={css.seeAll}>
            <FormattedMessage id="SectionCategories.seeAll" />
          </NamedLink>
        </div>

        <ul className={css.grid}>
          {items.map(cat => {
            // Tolerant lookup: exact id first, then a slugified label (covers the fallback config
            // branch whose option keys can differ from the categoryLevel1 ids).
            const slug = (cat.label || '')
              .toLowerCase()
              .replace(/&/g, 'and')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
            const img = CATEGORY_IMAGES[cat.key] || CATEGORY_IMAGES[slug];
            return (
              <li key={cat.key} className={css.item}>
                <NamedLink
                  name="SearchPage"
                  to={{ search: `?${param}=${encodeURIComponent(cat.key)}` }}
                  className={css.tile}
                >
                  <span className={css.tileMedia} aria-hidden="true">
                    {img ? (
                      <>
                        {/* <picture>: modern browsers take the small AVIF source; browsers that
                            can't decode AVIF (iOS Safari <16, many in-app WebViews) fall back to
                            the JPEG <img> -- one format is downloaded, never both.
                            Deliberately NOT loading="lazy": these tiles sit near the top of the
                            landing page inside an opacity-0 entrance animation, and lazy-load
                            intersection logic misfires on animated/transparent ancestors -- the
                            fetch was nondeterministic (photos randomly missing until refresh).
                            Explicit dimensions match .tileMedia's 16/10 box. */}
                        <picture>
                          <source srcSet={img.avif} type="image/avif" />
                          <img
                            className={css.tileImg}
                            src={img.jpg}
                            alt=""
                            width="320"
                            height="200"
                            decoding="async"
                          />
                        </picture>
                        <span className={css.tileOverlay} />
                      </>
                    ) : null}
                  </span>
                  <span className={css.tileLabel}>{cat.label}</span>
                </NamedLink>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default SectionCategories;
